/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, EventEmitter, Output, Input, ChangeDetectorRef,ViewChild} from '@angular/core';

import {BaseInstance} from "../../../models/base-instance";
import {Function} from '../../../models/function';
import {FunctionParameter} from '../../../models/function-parameter';
import {GroupType} from "../../../models/group-type";
import {PropertyPatch} from "../../../models/property-patch";

import {AntennaService} from "../../../services/antenna.service";
import {LocationService} from "../../../services/location.service";
import {ObjectService} from "../../../services/object.service";
import {ReaderService} from "../../../services/reader.service";
import {ServiceService} from "../../../services/service.service";
import {TagService} from "../../../services/tag.service";
import {ZoneService} from "../../../services/zone.service";
import {PropertyType} from 'app/models/property-type';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, of } from 'rxjs';
import {FunctionExecutingDialogComponent} from "../dialogs/function-executing-dialog.component";

declare var $: any;


@Component({
  selector: 'app-instance-function',
  templateUrl: './instance-function.component.html'
})
export class InstanceFunctionComponent implements OnInit {
  
@ViewChild('functionDialog')
functionDialog!: FunctionExecutingDialogComponent;

  @Input()  //Type of Instance whose Function is being modeled and could be called.  Used to figure out what Service to call
  instanceType: GroupType;
  
  @Input() // Instance the Function would be called on if called
  instance: BaseInstance;
  
  @Input()  //Function that is being modeled
  function: Function;
  
  @Output() //Called when the User starts executing a Function.  The function being executed is passed
  functionExecutionStartEvent = new EventEmitter<Function>();
  
  @Output() //Called when a Function finishes executing successfully
  functionExecutionSuccessEvent = new EventEmitter<any>();
  
  @Output() //Called when a Function finishes executing but with an error.  The error message is passed.
  functionExecutionErrorEvent = new EventEmitter<string>();
  
  // Function Data, Holds the data for the parameters to call the function with
  functionParameterData: FunctionParameterDataUi[] = [];
  pageSize = 20;
  start = 0;
  order = 'ASC';

  // This flag controls showing/hiding the parameter pane
  mParametersDisplayed = false;
  
  
  constructor(
    private mAntennaService: AntennaService,
    private mLocationService: LocationService,
    private mObjectService: ObjectService,
    private mReaderService: ReaderService,
    private mServiceService: ServiceService,
    private mTagService: TagService,
    private mZoneService: ZoneService
  ) { }
  
  
  ngOnInit(): void {
    this.refreshData();
  }

  /** Button click logic */
  buttonClicked(): void {
    if (this.function?.parameters?.length > 0) {
      this.refreshData();          // Ensure parameters are loaded
      this.toggleParameters();     // Show/hide the pane
    } else {
      this.executeFunction();      // No parameters, execute immediately
    }
  }

  toggleParameters(): void {
    this.mParametersDisplayed = !this.mParametersDisplayed;
  }

  getFunctionParametersTabId(): string {
    return `function_${this.function?.id}_parameters`;
  }

  /** Prepare parameter data for UI */
  refreshData(): void {
    const data: FunctionParameterDataUi[] = [];

    if (this.function?.parameters?.length > 0) {
      for (const parameter of this.function.parameters) {
        const newData = new FunctionParameterDataUi();
        newData.functionParameter = parameter;

        const dataUi = new DataObjectUi();
        dataUi.value = '';
        dataUi.object_value = null;
        dataUi.availableInstances = [];
        dataUi.instanceResults$ = of([]);

        newData.dataObjects.push(dataUi);
        data.push(newData);
      }
    }

    this.functionParameterData.length = 0;
    this.functionParameterData.push(...data);
  }

  /** Check reference type */
  isReferenceType(type: PropertyType): boolean {
    return [
      PropertyType.LOCATION_REFERENCE,
      PropertyType.ZONE_REFERENCE,
      PropertyType.READER_REFERENCE,
      PropertyType.TAG_REFERENCE,
      PropertyType.OBJECT_REFERENCE
    ].includes(type);
  }

  /** Typeahead input change */
  onInputChange(value: string, datum: DataObjectUi, parameter: FunctionParameter) {
    if (!value || value.length < 2) {
      datum.availableInstances = [];
      datum.instanceResults$ = of([]);
      return;
    }

    let request: Promise<any[]>;

    switch (parameter.type) {
      case PropertyType.LOCATION_REFERENCE:
        request = this.mLocationService.getLocationInstancesFiltered(this.start, this.pageSize, this.order, value);
        break;
      case PropertyType.ZONE_REFERENCE:
        request = this.mZoneService.getZoneInstancesFiltered(this.start, this.pageSize, this.order, value);
        break;
      case PropertyType.READER_REFERENCE:
        request = this.mReaderService.getReaderInstancesFiltered(this.start, this.pageSize, this.order, value);
        break;
      case PropertyType.TAG_REFERENCE:
        request = this.mTagService.getTagInstancesFiltered(this.start, this.pageSize, this.order, value);
        break;
      case PropertyType.OBJECT_REFERENCE:
        request = this.mObjectService.getObjectInstancesFiltered(this.start, this.pageSize, this.order, value);
        break;
      default:
        datum.availableInstances = [];
        datum.instanceResults$ = of([]);
        return;
    }

    request.then(results => {
      datum.availableInstances = results;
      datum.instanceResults$ = of(results);
    });
  }

  /** Typeahead selection */
  onInstanceSelected(event: TypeaheadMatch, datum: DataObjectUi) {
    datum.object_value = event.item;
    datum.value = event.item.id;
    datum.instanceResults$ = of([]);
  }

  /** Multi-value add/remove */
  addNewValue(paramData: FunctionParameterDataUi) {
    const newDatum = new DataObjectUi();
    newDatum.value = '';
    newDatum.object_value = null;
    newDatum.instanceResults$ = of([]);
    paramData.dataObjects.push(newDatum);
  }

  removeValueAtIndex(paramData: FunctionParameterDataUi, index: number) {
    paramData.dataObjects.splice(index, 1);
  }

  /** Execute the function */
  async executeFunction(): Promise<void> {
  const data: PropertyPatch[] = [];

  // Build PropertyPatch[] from function parameters
  for (const functionParameterData of this.functionParameterData) {
    for (const datum of functionParameterData.dataObjects) {
      if (!datum?.value) continue;

      const propertyPatch = new PropertyPatch();
      propertyPatch.property = functionParameterData.functionParameter.key;

      switch (functionParameterData.functionParameter.type) {
        case PropertyType.OBJECT_REFERENCE:
        case PropertyType.LOCATION_REFERENCE:
        case PropertyType.ZONE_REFERENCE:
        case PropertyType.READER_REFERENCE:
        case PropertyType.TAG_REFERENCE:
          propertyPatch.value = datum.object_value?.id || datum.value;
          break;
        default:
          propertyPatch.value = datum.value;
      }

      data.push(propertyPatch);
    }
  }

  this.functionExecutionStartEvent.emit(this.function);

  if (!this.instanceType) {
    this.handleError("No Instance Type to determine API.");
    return;
  }

  try {
    let response: any;
    const type = this.function?.result?.type;
    // Call the appropriate service based on instance type
    switch (this.instanceType) {
      case GroupType.ANTENNAINSTANCE:
        response = await this.mAntennaService.executeFunction(this.instance.id, this.function.id, data);
        break;
      case GroupType.LOCATIONINSTANCE:
        response = await this.mLocationService.executeFunction(this.instance.id, this.function.id, data);
        break;
      case GroupType.OBJECTINSTANCE:
        if (type !== null && type !== undefined) {
          response = await this.mObjectService.executeFunction(this.instance.id, this.function.id, data,'text');
        }else{
          response = await this.mObjectService.executeFunction(this.instance.id, this.function.id, data);
        }
        
        break;
      case GroupType.READERINSTANCE:
        response = await this.mReaderService.executeFunction(this.instance.id, this.function.id, data);
        break;
      case GroupType.SERVICEINSTANCE:
        response = await this.mServiceService.executeFunction(this.instance.id, this.function.id, data);
        break;
      case GroupType.TAG:
        response = await this.mTagService.executeFunction(this.instance.id, this.function.id, data);
        break;
      case GroupType.READZONEINSTANCE:
        response = await this.mZoneService.executeFunction(this.instance.id, this.function.id, data);
        break;
      default:
        this.handleError("Invalid instance type.");
        return ; 
    }

    // Pass response to success handler
    this.handleSuccess(response);

  } catch (err: any) {
    this.handleError(err?.error || err?.message || 'Unknown error');
  }
}

// Updated handleSuccess to accept response
handleSuccess(response?: any) {
  this.functionExecutionSuccessEvent.emit(response);
  this.refreshData();
}

handleError(error: any) {
  this.functionExecutionErrorEvent.emit(error);
}

}

/**
 * UI Model for a Function Parameter's and it's Data
 */
export class FunctionParameterDataUi {
  functionParameter: FunctionParameter;
  dataObjects: DataObjectUi[] = [];
}

/**
 * UI Model for the Data of a Function Parameter (or Property for that matter)
 */
export class DataObjectUi {
  value: string;
  object_value: any;
  availableInstances: any[] = [];
  instanceResults$: Observable<any[]> = of([]);
}
